/**
 * Client error funnel — shared across all SBI SPAs (EPIC #5310 / #5309 join).
 *
 * SBI captures errors to PostHog at the DOMAIN level already (Zaraz/posthog-js,
 * project 102930). This module does NOT add a PostHog credential or a new
 * instance — it reuses the existing `window.posthog`. Its job is the JOIN: make
 * a CLIENT-observed fault carry the failing response's `x-trace-id` (chain-wide
 * join key) and `x-request-id` (per-hop cf-ray) so it lines up with that fault's
 * server records (CF Observability `[request_trace]` + `sbi-logs-db` row, #5309).
 * Each fault fans out to:
 *   1. POST /api/client-errors  → CF Observability `[CLIENT-ERROR]` + durable D1
 *   2. window.posthog.captureException(…, { trace_id, request_id, error_code }) → 102930
 *
 * Same-origin only: each SPA posts to its own worker's `/api/client-errors`
 * (every SBI worker that serves a browser surface hosts that endpoint + binds
 * LOG_DB). The fetch wrapper is a pure passthrough — it never alters the response
 * or the error, and all reporting is best-effort inside try/catch.
 *
 * Extracted from support-sbi's verified implementation so all SPAs (www-sbi,
 * admin-sbi, support-sbi, brand-sbi) share ONE funnel. Browser-only — never
 * imported by a Worker bundle (those use `@sbs/errors/observability`).
 */

const DEDUP_WINDOW_MS = 5000;
const recent = new Map<string, number>();
const BENIGN_ERROR_PATTERNS: RegExp[] = [/ResizeObserver loop/i];
let seq = 0;

export interface ClientErrorCode {
  /** Canonical user-facing lookup key shared across CF Observability and PostHog. */
  errorCode: string;
  /** Backwards-compatible alias used by the existing durable `error_ref` column. */
  errorRef: string;
  /** ISO timestamp recorded as a separate filterable property. */
  errorTimestamp: string;
}

/** User-facing code: EB-<base36 time>-<base36 seq>, uppercased. */
export function makeErrorCode(now = new Date()): ClientErrorCode {
  const errorCode = `EB-${now.getTime().toString(36)}-${(seq++).toString(36)}`.toUpperCase();
  return {
    errorCode,
    errorRef: errorCode,
    errorTimestamp: now.toISOString(),
  };
}

/** Backwards-compatible helper for older callers. Prefer makeErrorCode(). */
export function makeErrorRef(): string {
  return makeErrorCode().errorRef;
}

function isBenign(message: string): boolean {
  return BENIGN_ERROR_PATTERNS.some((re) => re.test(message));
}

export type ClientErrorVariant = 'app' | 'page' | 'inline' | 'global' | 'api';

export interface ClientErrorReport {
  component: string;
  message: string;
  stack?: string | null;
  url?: string;
  errorRef?: string;
  /** Canonical lookup key. Falls back to errorRef for older callers. */
  errorCode?: string;
  /** ISO timestamp for the specific generated error code. */
  errorTimestamp?: string;
  /** #5309 — the failing response's chain-wide x-trace-id (the primary join key). */
  traceId?: string | null;
  /** #5309 — the failing response's per-hop x-request-id (cf-ray). */
  requestId?: string | null;
  variant?: ClientErrorVariant;
  errorType?: string | null;
  statusCode?: number | null;
}

interface PosthogLike {
  captureException?: (error: unknown, properties?: Record<string, unknown>) => void;
  capture?: (event: string, properties?: Record<string, unknown>) => void;
}

function capturePosthog(report: ClientErrorReport, error: Error): void {
  try {
    const errorCode = report.errorCode ?? report.errorRef ?? null;
    const ph = (window as unknown as { posthog?: PosthogLike }).posthog;
    const properties = {
      error_code: errorCode,
      error_timestamp: report.errorTimestamp ?? null,
      error_ref: report.errorRef ?? errorCode,
      trace_id: report.traceId ?? null,
      request_id: report.requestId ?? null,
      $exception_source: report.variant === 'api' ? 'client-api' : 'client',
      component: report.component,
      variant: report.variant ?? 'global',
      route: report.url ?? null,
      status_code: report.statusCode ?? null,
    };
    if (typeof ph?.captureException === 'function') {
      ph.captureException(error, properties);
    } else if (typeof ph?.capture === 'function') {
      ph.capture('client_error', {
        ...properties,
        error_message: report.message,
        error_name: error.name,
        error_stack: report.stack ?? null,
      });
    }
  } catch {
    /* never let telemetry break the app */
  }
}

/**
 * Telemetry URL redaction (#230). Byte-for-byte copy of the canonical,
 * unit-tested `sanitizeUrlForTelemetry` in `@sbs/utils`
 * (libs/utils/src/lib/url-sanitize.ts). It is kept LOCAL — not imported from
 * `@sbs/utils` — because that barrel is imported by the DOM-less Worker apps and
 * browser-adjacent code stays out of it (see `clipboard.ts`). Keep in sync with
 * the canonical copy.
 */
const SANITIZE_REDACTED = '[redacted]';
const SANITIZE_SENSITIVE_PARAM_NAME =
  /(^|[_-])(access_?token|refresh_?token|provider_?token|id_?token|token|code|state|session|sess|sid|auth|password|passwd|pwd|secret|api_?key|apikey|key|jwt|bearer|email|otp|nonce|assertion|credential|signature|sig)([_-]|$)/i;
const SANITIZE_JWT_SHAPE = /eyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{2,}/;
const SANITIZE_JWT_SHAPE_GLOBAL = new RegExp(SANITIZE_JWT_SHAPE.source, 'g');
const SANITIZE_EMAIL_SHAPE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const SANITIZE_EMAIL_SHAPE_GLOBAL = new RegExp(SANITIZE_EMAIL_SHAPE.source, 'g');
const SANITIZE_LONG_SECRET_SHAPE = /[A-Za-z0-9_-]{24,}/;

function sanitizeValueLooksSensitive(value: string): boolean {
  return (
    SANITIZE_JWT_SHAPE.test(value) ||
    SANITIZE_EMAIL_SHAPE.test(value) ||
    SANITIZE_LONG_SECRET_SHAPE.test(value)
  );
}

function sanitizeUrlForTelemetry(
  input: string | null | undefined,
  options: { allowParams?: readonly string[] } = {},
): string | null {
  if (input == null) return null;
  const raw = typeof input === 'string' ? input.trim() : String(input);
  if (!raw) return null;
  let parsed: URL;
  try {
    parsed = new URL(raw, 'https://redacted.invalid');
  } catch {
    return '[redacted-url]';
  }
  const allow = new Set((options.allowParams ?? []).map((name) => name.toLowerCase()));
  const safeParams = new URLSearchParams();
  for (const [name, value] of parsed.searchParams) {
    if (!allow.has(name.toLowerCase())) continue;
    if (SANITIZE_SENSITIVE_PARAM_NAME.test(name)) continue;
    if (sanitizeValueLooksSensitive(value)) continue;
    safeParams.append(name, value);
  }
  // The fragment is always dropped — OAuth implicit-flow tokens live there.
  const isRelative = parsed.origin === 'https://redacted.invalid' || parsed.origin === 'null';
  const origin = isRelative ? '' : parsed.origin;
  const query = safeParams.toString();
  let result = `${origin}${parsed.pathname}${query ? `?${query}` : ''}`;
  result = result
    .replace(SANITIZE_JWT_SHAPE_GLOBAL, SANITIZE_REDACTED)
    .replace(SANITIZE_EMAIL_SHAPE_GLOBAL, SANITIZE_REDACTED);
  return result;
}

export function reportClientError(report: ClientErrorReport): void {
  if (isBenign(report.message)) return;

  // #230 — redact the URL ONCE, before it reaches ANY sink (the
  // /api/client-errors POST body below AND the PostHog capture). This choke
  // point covers every caller of the funnel: the global handlers, ErrorBoundary
  // (error-boundary.tsx), and any external caller.
  const safeUrl = sanitizeUrlForTelemetry(
    report.url ?? (typeof location !== 'undefined' ? location.href : null),
  );

  const generated = report.errorCode || report.errorRef ? null : makeErrorCode();
  const errorCode = report.errorCode ?? report.errorRef ?? generated?.errorCode ?? null;
  const errorRef = report.errorRef ?? errorCode ?? undefined;
  const errorTimestamp =
    report.errorTimestamp ?? generated?.errorTimestamp ?? new Date().toISOString();

  // Critical full-page crashes (variant 'app') bypass dedup so the EB- ref the
  // user is shown always persists; everything else dedups per component::message.
  if (report.variant !== 'app') {
    const key = `${report.component}::${report.message}`;
    const now = Date.now();
    const last = recent.get(key);
    if (last && now - last < DEDUP_WINDOW_MS) return;
    recent.set(key, now);
  }

  const err = new Error(report.message);
  if (report.stack) err.stack = report.stack;

  // Sink 1+2 (durable + CF Observability) via the server endpoint.
  try {
    void fetch('/api/client-errors', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        component: report.component,
        message: report.message,
        stack: report.stack ?? null,
        url: safeUrl,
        errorCode,
        errorRef,
        errorTimestamp,
        traceId: report.traceId ?? null,
        requestId: report.requestId ?? null,
        variant: report.variant ?? 'global',
        errorType: report.errorType ?? null,
        statusCode: report.statusCode ?? null,
      }),
    }).catch(() => {});
  } catch {
    /* best effort */
  }

  // Sink 3 (PostHog 102930) via the existing domain-level posthog-js.
  capturePosthog(
    { ...report, url: safeUrl ?? undefined, errorCode: errorCode ?? undefined, errorRef, errorTimestamp },
    err,
  );
}

let installed = false;

/** Install once at client startup (idempotent). */
export function installClientErrorTelemetry(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    if (!event.error && event.target && event.target !== window) return; // resource-load noise
    const err = event.error as Error | undefined;
    const message = err?.message || event.message || '(uncaught error)';
    if (isBenign(message)) return;
    const errorCode = makeErrorCode();
    reportClientError({
      component: 'window.onerror',
      message,
      stack: err?.stack ?? null,
      url: location.href,
      ...errorCode,
      variant: 'global',
      errorType: err?.name ?? null,
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason as unknown;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : (() => {
              try {
                return JSON.stringify(reason);
              } catch {
                return String(reason);
              }
            })();
    const resolved = message || '(unhandled rejection)';
    if (isBenign(resolved)) return;
    const errorCode = makeErrorCode();
    reportClientError({
      component: 'window.unhandledrejection',
      message: resolved,
      stack: reason instanceof Error ? (reason.stack ?? null) : null,
      url: location.href,
      ...errorCode,
      variant: 'global',
      errorType: reason instanceof Error ? reason.name : null,
    });
  });

  installFetchTelemetry();
}

/**
 * #5309 — auto-join a same-origin /api 5xx to its server trace. Pure passthrough:
 * the original fetch is bound to window, its result/throw is returned verbatim,
 * and the report is best-effort. Recursion is avoided by skipping the
 * /api/client-errors endpoint itself.
 */
function installFetchTelemetry(): void {
  const original = window.fetch?.bind(window);
  if (!original || (window.fetch as { __sbiWrapped?: boolean }).__sbiWrapped) return;

  const wrapped = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const res = await original(input, init);
    try {
      if (res.status >= 500) {
        const rawUrl =
          typeof input === 'string'
            ? input
            : input instanceof URL
              ? input.href
              : (input as Request).url;
        const parsed = (() => {
          try {
            return new URL(rawUrl, location.href);
          } catch {
            return null;
          }
        })();
        // Same-origin only (#6707): a cross-origin URL — e.g. the PostHog support
        // widget's https://*.i.posthog.com/api/conversations/... 5xx — shares our
        // /api/ path prefix but is not our API, so reporting it pollutes SBS
        // observability with fake 500s. If the URL can't be parsed we can't verify
        // origin, so we skip rather than risk mis-attributing it.
        if (parsed && parsed.origin === location.origin) {
          const path = parsed.pathname;
          if (path.startsWith('/api/') && path !== '/api/client-errors') {
            const errorCode = makeErrorCode();
            reportClientError({
              component: `api ${path}`,
              message: `API ${res.status} ${path}`,
              url: location.href,
              ...errorCode,
              traceId: res.headers.get('x-trace-id'),
              requestId: res.headers.get('x-request-id'),
              variant: 'api',
              statusCode: res.status,
            });
          }
        }
      }
    } catch {
      /* telemetry must never affect the fetch */
    }
    return res;
  };
  (wrapped as { __sbiWrapped?: boolean }).__sbiWrapped = true;
  window.fetch = wrapped as typeof window.fetch;
}
